import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  List,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Toolbar,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import { SelfHostedEditor } from "../../../components/TextEditor";
import { ImageCropField } from "../../../components/ImageCropField";
import { useImageCrop } from "../../../hooks";
import { ApiPost, ApiPut } from "../../../helpers/API/ApiData";

export const defaultInputValue = {
  title: "",
  title_ger: "",
  desc: "",
  desc_ger: "",
  price: "",
  image: "",
  image_ger: "",
};

type Props = {
  isOpen: boolean;
  loading: boolean;
  isEditing: boolean;
  editData: typeof defaultInputValue;
  idForChallengeEdit: string;
  onClose: () => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getNewsDetails: () => void;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  handleOnInputFieldChange: (e: any) => void;
};

export const ChallengeForm = ({
  isOpen,
  loading,
  isEditing,
  editData,
  idForChallengeEdit,
  onClose,
  setLoading,
  setModalOpen,
  getNewsDetails,
  setEditing,
}: Props) => {
  const [inputValue, setInputValue] = useState(editData || defaultInputValue);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (editData) {
      setInputValue(editData);
    }
  }, [editData]);

  const imageCropEn = useImageCrop();
  const imageCropGer = useImageCrop();
  const [removeImageEn, setRemoveImageEn] = useState(false);
  const [removeImageGer, setRemoveImageGer] = useState(false);

  const handleOnChange = (e: any) => {
    const { name, value } = e.target;
    setInputValue({ ...inputValue, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleClose = () => {
    imageCropEn.reset();
    imageCropGer.reset();
    setRemoveImageEn(false);
    setRemoveImageGer(false);
    setErrors({});
    onClose();
  };

  const formValidation = () => {
    let formIsValid = true;
    const err: any = {};

    if (!inputValue?.title?.trim()) {
      formIsValid = false;
      err["title"] = "*Bitte geben Sie den Namen ein";
    }
    if (!inputValue?.title_ger?.trim()) {
      formIsValid = false;
      err["title_ger"] = "*Bitte geben Sie den Namen ein";
    }
    if (!inputValue?.price) {
      formIsValid = false;
      err["price"] = "*Bitte geben Sie den Preis ein";
    }
    if (!imageCropEn.imageToUpload && !inputValue?.image) {
      formIsValid = false;
      err["image"] = "*Bitte wählen Sie ein Bild aus";
    }
    if (!imageCropGer.imageToUpload && !inputValue?.image_ger) {
      formIsValid = false;
      err["image_ger"] = "*Bitte wählen Sie ein Bild aus";
    }

    setErrors(err);
    return formIsValid;
  };

  const handleSave = async () => {
    if (!formValidation()) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", inputValue?.title);
      formData.append("title_ger", inputValue?.title_ger);
      formData.append("desc", inputValue?.desc);
      formData.append("desc_ger", inputValue?.desc_ger);
      formData.append("price", inputValue?.price);

      const croppedEn = await imageCropEn.getCroppedFile();
      if (croppedEn) formData.append("image", croppedEn);
      const croppedGer = await imageCropGer.getCroppedFile();
      if (croppedGer) formData.append("image_ger", croppedGer);
      if (isEditing && removeImageEn) formData.append("removeImage", "true");
      if (isEditing && removeImageGer) formData.append("removeImage_ger", "true");

      if (isEditing) {
        await ApiPut(`redeemCoins/updateData?id=${idForChallengeEdit}`, formData);
        setEditing(false);
        toast.success("Erfolgreich aktualisiert!");
      } else {
        await ApiPost(`redeemCoins/createData`, formData);
        toast.success("Fügten erfolgreich hinzu!");
      }

      setModalOpen(false);
      imageCropEn.reset();
      imageCropGer.reset();
      setRemoveImageEn(false);
      setRemoveImageGer(false);
      setInputValue(defaultInputValue);
      getNewsDetails();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullScreen>
      <Toolbar>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <List>
        <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Deutsch
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                English
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Name</Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                id="title_ger"
                name="title_ger"
                value={inputValue?.title_ger}
                onChange={handleOnChange}
              />
              <Typography variant="caption" color="error">
                {errors["title_ger"]}
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                id="title"
                name="title"
                value={inputValue?.title}
                onChange={handleOnChange}
              />
              <Typography variant="caption" color="error">
                {errors["title"]}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
              Beschreibung
            </Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <SelfHostedEditor
                onChange={(_event: any, editor: any) => {
                  setInputValue((prev) => ({
                    ...prev,
                    desc_ger: editor?.getContent(),
                  }));
                }}
                initialValue={inputValue?.desc_ger}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <SelfHostedEditor
                onChange={(_event: any, editor: any) => {
                  setInputValue((prev) => ({
                    ...prev,
                    desc: editor?.getContent(),
                  }));
                }}
                initialValue={inputValue?.desc}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Preis</Typography>
            <Box sx={{ width: { xs: "100%", lg: "83.33%" } }}>
              <TextField
                type="number"
                size="small"
                fullWidth
                variant="outlined"
                id="price"
                name="price"
                value={inputValue?.price}
                onChange={handleOnChange}
              />
              <Typography variant="caption" color="error">
                {errors["price"]}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
            <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>Bild</Typography>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <ImageCropField
                inputId="image_ger"
                imageCrop={imageCropGer}
                existingImage={typeof inputValue?.image_ger === "string" ? inputValue?.image_ger : undefined}
                error={errors["image_ger"]}
                onRemoveExistingImage={() => {
                  setInputValue((prev) => ({ ...prev, image_ger: "" }));
                  setRemoveImageGer(true);
                }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
              <ImageCropField
                inputId="imageUpload"
                imageCrop={imageCropEn}
                existingImage={typeof inputValue?.image === "string" ? inputValue?.image : undefined}
                error={errors["image"]}
                onRemoveExistingImage={() => {
                  setInputValue((prev) => ({ ...prev, image: "" }));
                  setRemoveImageEn(true);
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {loading ? (
            <Button variant="contained" disabled>
              {isEditing ? "Speichern" : "Hinzufügen"}
              <CircularProgress size={20} sx={{ ml: 2 }} />
            </Button>
          ) : (
            <Button variant="contained" sx={{ mr: 2 }} onClick={handleSave}>
              {isEditing ? "Speichern" : "Hinzufügen"}
            </Button>
          )}
        </Box>
      </List>
    </Dialog>
  );
};
